import { useState } from "react";
import { toast } from "sonner";
import { Calculator, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

// Rough indicative estimator based on ASF average tariffs (RON / 12 months).
// IMPORTANT: This is a guidance estimate; the real offer is computed by insurers.
const COUNTY_FACTOR = {
    "Bucuresti / Ilfov": 1.45,
    "Cluj / Timis / Constanta": 1.15,
    "Iasi / Brasov / Sibiu": 1.05,
    "Alt judet (urban mediu)": 1.0,
    "Mediu rural / oras mic": 0.9,
};

const AGE_FACTOR = {
    "sub-25": 1.55,
    "25-30": 1.18,
    "31-45": 1.0,
    "46-60": 0.92,
    "60-plus": 1.05,
};

const CAR_AGE_FACTOR = {
    "0-3": 1.10,
    "4-7": 1.0,
    "8-12": 0.92,
    "13-plus": 0.88,
};

// Base price by engine capacity (cm3) for a "middle" profile
function basePrice(cc) {
    if (cc <= 1200) return 520;
    if (cc <= 1400) return 680;
    if (cc <= 1600) return 880;
    if (cc <= 1800) return 1150;
    if (cc <= 2000) return 1450;
    if (cc <= 2500) return 1850;
    return 2400;
}

function estimateRCA({ cc, county, ownerAge, carAge }) {
    const base = basePrice(Number(cc));
    const cFac = COUNTY_FACTOR[county] ?? 1;
    const aFac = AGE_FACTOR[ownerAge] ?? 1;
    const carFac = CAR_AGE_FACTOR[carAge] ?? 1;
    const yearly = Math.round(base * cFac * aFac * carFac);
    return {
        min: Math.round(yearly * 0.85),
        avg: yearly,
        max: Math.round(yearly * 1.20),
    };
}

const AGE_LABELS = {
    "sub-25": "Sub 25 ani",
    "25-30": "25 – 30 ani",
    "31-45": "31 – 45 ani",
    "46-60": "46 – 60 ani",
    "60-plus": "Peste 60 ani",
};
const CAR_AGE_LABELS = {
    "0-3": "0 – 3 ani (nouă)",
    "4-7": "4 – 7 ani",
    "8-12": "8 – 12 ani",
    "13-plus": "Peste 13 ani",
};
export default function RCACalculator() {
    const [cc, setCc] = useState("1600");
    const [county, setCounty] = useState("Alt judet (urban mediu)");
    const [ownerAge, setOwnerAge] = useState("31-45");
    const [carAge, setCarAge] = useState("4-7");
    const [estimate, setEstimate] = useState(null);

    // lead form (shown after estimate)
    const [lead, setLead] = useState({ name: "", phone: "", email: "" });
    const [submitting, setSubmitting] = useState(false);

    const calculate = (e) => {
        e?.preventDefault?.();
        const ccNum = Number(cc);
        if (!ccNum || ccNum < 600 || ccNum > 6000) {
            toast.error("Capacitatea cilindrică trebuie să fie între 600 și 6000 cm³.");
            return;
        }
        setEstimate(estimateRCA({ cc: ccNum, county, ownerAge, carAge }));
        setTimeout(() => {
            document.getElementById("rca-result")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 100);
    };

    const submitLead = async (e) => {
        e.preventDefault();
        if (!lead.name || !lead.phone || !lead.email) {
            toast.error("Te rog completează nume, telefon și email.");
            return;
        }
        const summary =
            `Estimare RCA calculator:\n` +
            `• Capacitate cilindrică: ${cc} cm³\n` +
            `• Județ: ${county}\n` +
            `• Vârsta proprietarului: ${AGE_LABELS[ownerAge]}\n` +
            `• Vârsta mașinii: ${CAR_AGE_LABELS[carAge]}\n` +
            `• Estimare orientativă: ${estimate.min} – ${estimate.max} RON/an (medie ${estimate.avg} RON).\n\n` +
            `Doresc oferta REALĂ de la mai mulți asigurători.`;
        setSubmitting(true);
        try {
            await api.post("/quote-requests", {
                name: lead.name,
                phone: lead.phone,
                email: lead.email,
                product_type: "RCA / CASCO",
                message: summary,
            });
            toast.success("Cererea ta a fost trimisă! Te contactăm în maxim 24h cu oferte reale de la 5+ asigurători.");
            setLead({ name: "", phone: "", email: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section
            id="rca"
            data-testid="rca-section"
            className="bg-[#f5f3ec] py-24 lg:py-32 relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="grid lg:grid-cols-12 gap-10 mb-12">
                    <div className="lg:col-span-5">
                        <div className="text-xs uppercase tracking-[0.2em] text-[#0d3d2e]/60 mb-4 inline-flex items-center gap-2">
                            <Calculator className="h-3.5 w-3.5" strokeWidth={1.5} />
                            Calculator instant
                        </div>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#0d3d2e] leading-[1.05]">
                            Calculează<br />prima ta RCA<br />
                            <em className="not-italic text-[#0d3d2e]/55">în 10 secunde.</em>
                        </h2>
                    </div>
                    <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                        <p className="text-base lg:text-lg text-[#1a1a1a]/70">
                            Introduceți câteva detalii despre mașină și șofer și vă afișăm o estimare
                            orientativă pe baza tarifelor medii ASF. Pentru oferte personalizate de la
                            5+ asigurători, lăsați-ne datele de contact și vă contactăm în maxim 24h.
                        </p>
                    </div>
                </div>

                <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
                    {/* Inputs */}
                    <form
                        onSubmit={calculate}
                        data-testid="rca-form"
                        className="lg:col-span-7 bg-white border border-[#0d3d2e]/15 rounded-2xl p-6 lg:p-10 space-y-5"
                    >
                        <div className="grid sm:grid-cols-2 gap-5">
                            <div>
                                <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                    Capacitate cilindrică (cm³)
                                </label>
                                <input
                                    type="number"
                                    step="50"
                                    value={cc}
                                    onChange={(e) => setCc(e.target.value)}
                                    data-testid="rca-cc"
                                    className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e]"
                                    placeholder="1600"
                                />
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                    Județ
                                </label>
                                <select
                                    value={county}
                                    onChange={(e) => setCounty(e.target.value)}
                                    data-testid="rca-county"
                                    className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e]"
                                >
                                    {Object.keys(COUNTY_FACTOR).map((c) => (
                                        <option key={c} value={c}>{c}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                    Vârsta proprietarului
                                </label>
                                <select
                                    value={ownerAge}
                                    onChange={(e) => setOwnerAge(e.target.value)}
                                    data-testid="rca-owner-age"
                                    className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e]"
                                >
                                    {Object.entries(AGE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-[0.16em] text-[#0d3d2e]/70 mb-2">
                                    Vechimea autoturismului
                                </label>
                                <select
                                    value={carAge}
                                    onChange={(e) => setCarAge(e.target.value)}
                                    data-testid="rca-car-age"
                                    className="w-full bg-[#fcfbf8] border border-[#0d3d2e]/20 rounded-md px-4 py-3 text-[#0d3d2e] focus:outline-none focus:border-[#0d3d2e]"
                                >
                                    {Object.entries(CAR_AGE_LABELS).map(([k, v]) => (
                                        <option key={k} value={k}>{v}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <button
                            type="submit"
                            data-testid="rca-calc-btn"
                            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#0d3d2e] text-[#fcfbf8] px-8 py-3.5 text-base font-medium hover:bg-[#0d3d2e]/90 transition-all"
                        >
                            <Sparkles className="h-4 w-4" strokeWidth={1.75} />
                            Calculează estimarea
                        </button>

                        <p className="text-xs text-[#1a1a1a]/55 flex items-start gap-2">
                            <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                            Estimare orientativă bazată pe tarifele medii ASF din 2025. Prețul real depinde
                            de istoricul de daune, bonus-malus, perioada de asigurare și asigurător.
                        </p>
                    </form>

                    {/* Result */}
                    <div
                        id="rca-result"
                        data-testid="rca-result"
                        className="lg:col-span-5 bg-[#0d3d2e] text-[#fcfbf8] rounded-2xl relative overflow-hidden min-h-[400px] flex flex-col"
                    >
                        <img
                            src="/images/rca-car.webp"
                            alt=""
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div
                            className={`absolute inset-0 ${
                                estimate
                                    ? "bg-gradient-to-b from-[#0d3d2e]/90 via-[#0d3d2e]/95 to-[#0d3d2e]"
                                    : "bg-gradient-to-b from-[#0d3d2e]/25 via-[#0d3d2e]/45 to-[#0d3d2e]/90"
                            }`}
                        />
                        <div className="relative z-10 flex flex-col flex-1 p-6 lg:p-10">
                        {!estimate ? (
                            <div className="mt-auto text-[#fcfbf8]">
                                <div className="inline-flex items-center gap-2 rounded-full bg-[#fcfbf8]/15 backdrop-blur px-3.5 py-1.5 text-xs uppercase tracking-[0.16em] mb-4">
                                    <Calculator className="h-3.5 w-3.5" strokeWidth={1.5} />
                                    Estimare instant
                                </div>
                                <p className="font-display text-2xl lg:text-3xl leading-snug">
                                    Completează datele din stânga și apasă „Calculează estimarea".
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-6">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-[#d4af37] mb-2">
                                        Estimare orientativă RCA
                                    </div>
                                    <div className="font-display text-5xl lg:text-6xl text-[#fcfbf8] leading-none">
                                        {estimate.min}–{estimate.max}
                                        <span className="text-2xl lg:text-3xl text-[#fcfbf8]/65 ml-2">RON/an</span>
                                    </div>
                                    <div className="text-sm text-[#fcfbf8]/65 mt-3">
                                        Medie indicativă: <span className="text-[#d4af37] font-medium">{estimate.avg} RON</span> · estimare 12 luni
                                    </div>
                                </div>

                                <div className="border-t border-[#fcfbf8]/15 pt-5">
                                    <div className="font-display text-xl text-[#fcfbf8] mb-1">
                                        Vrei oferta REALĂ?
                                    </div>
                                    <p className="text-sm text-[#fcfbf8]/70 mb-5">
                                        Lasă-ne datele de contact și te contactăm cu oferte personalizate
                                        de la 5+ asigurători. Gratuit, fără angajament, în maxim 24h.
                                    </p>
                                    <form onSubmit={submitLead} className="space-y-3" data-testid="rca-lead-form">
                                        <input
                                            type="text"
                                            placeholder="Nume complet"
                                            value={lead.name}
                                            onChange={(e) => setLead({ ...lead, name: e.target.value })}
                                            required
                                            data-testid="rca-lead-name"
                                            className="w-full bg-[#fcfbf8]/10 border border-[#fcfbf8]/20 rounded-md px-4 py-2.5 text-[#fcfbf8] placeholder-[#fcfbf8]/50 focus:outline-none focus:border-[#d4af37]"
                                        />
                                        <div className="grid grid-cols-2 gap-3">
                                            <input
                                                type="tel"
                                                placeholder="Telefon"
                                                value={lead.phone}
                                                onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                                                required
                                                data-testid="rca-lead-phone"
                                                className="w-full bg-[#fcfbf8]/10 border border-[#fcfbf8]/20 rounded-md px-4 py-2.5 text-[#fcfbf8] placeholder-[#fcfbf8]/50 focus:outline-none focus:border-[#d4af37]"
                                            />
                                            <input
                                                type="email"
                                                placeholder="Email"
                                                value={lead.email}
                                                onChange={(e) => setLead({ ...lead, email: e.target.value })}
                                                required
                                                data-testid="rca-lead-email"
                                                className="w-full bg-[#fcfbf8]/10 border border-[#fcfbf8]/20 rounded-md px-4 py-2.5 text-[#fcfbf8] placeholder-[#fcfbf8]/50 focus:outline-none focus:border-[#d4af37]"
                                            />
                                        </div>
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid="rca-lead-submit"
                                            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#d4af37] text-[#1a1a1a] px-6 py-3 text-sm font-semibold hover:bg-[#e0bd49] transition-colors disabled:opacity-60"
                                        >
                                            {submitting ? "Se trimite..." : "Vreau oferta reală"}
                                            <ArrowRight className="h-4 w-4" strokeWidth={2} />
                                        </button>
                                    </form>
                                </div>
                            </div>
                        )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
